import { CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { EmbeddingTypeFilter } from '../pages/embeddings/components/EmbeddingTypeFilter';
import { ImageZoomModal } from '../pages/embeddings/components/ImageZoomModal';
import { ApiService } from '../services/api';
import { ClassificationType, CropEmbeddingSearchResponse, EmbeddingSearchParams } from '../types';
import { EmbeddingsLink } from './EmbeddingsLink';

interface EmbeddingsListProps {
  normalizedUpc: string;
  chainId: number;
  storeId?: number;
  storeName?: string;
}

export const EmbeddingsList: React.FC<EmbeddingsListProps> = ({
  normalizedUpc,
  chainId,
  storeId,
  storeName,
}) => {
  const [embeddings, setEmbeddings] = useState<CropEmbeddingSearchResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ClassificationType | 'all'>('all');
  const [zoomImageSrc, setZoomImageSrc] = useState<string | null>(null);
  const { token } = useAuthContext();

  useEffect(() => {
    const fetchEmbeddings = async () => {
      if (!token || !normalizedUpc || !chainId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const params: EmbeddingSearchParams = {
          chainId,
          normalizedUpcs: normalizedUpc,
          limit: 100,
          isActive: true,
          extendedSearch: true,
          includeItemNumber: false,
        };

        const data = await ApiService.searchEmbeddings(token, params);
        setEmbeddings(data);
      } catch {
        setError('Failed to load embeddings');
      } finally {
        setLoading(false);
      }
    };

    fetchEmbeddings();
  }, [normalizedUpc, chainId, token]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-4 text-red-600 text-sm">{error}</div>;
  }

  const filteredEmbeddings =
    typeFilter === 'all'
      ? embeddings
      : embeddings.filter((e) => e.classificationType === typeFilter);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900">
          Embeddings ({filteredEmbeddings.length})
        </h2>
        <EmbeddingTypeFilter value={typeFilter} onChange={setTypeFilter} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8">
        {filteredEmbeddings.map((embedding) => (
          <div key={embedding.productEmbeddingCropId} className="relative">
            <div
              className="flex items-center justify-center relative rounded-lg overflow-hidden border"
              style={{
                height: 'calc(100% - 35px)',
                borderColor: '#D4D4D4',
              }}
            >
              <img
                src={embedding.vizUrl}
                alt={`Embedding ${embedding.productEmbeddingCropId}`}
                className="w-auto h-auto cursor-zoom-in"
                onClick={() => setZoomImageSrc(embedding.vizUrl)}
              />
            </div>
            <div className="flex items-center justify-center gap-4 mt-2 text-sm">
              <div className="flex">
                <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                  Active
                </span>
              </div>
              <div className="flex">
                <span
                  className="px-2 py-1 text-xs font-medium rounded capitalize"
                  style={{
                    backgroundColor: '#CEC9E8',
                    color: '#27272E',
                  }}
                >
                  {embedding.classificationType}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center mt-6 pt-4 border-t border-gray-200">
        <EmbeddingsLink
          upc={normalizedUpc}
          chainId={chainId}
          storeId={storeId}
          storeName={storeName}
        />
      </div>

      <ImageZoomModal
        isOpen={!!zoomImageSrc}
        imageSrc={zoomImageSrc || ''}
        onClose={() => setZoomImageSrc(null)}
      />
    </div>
  );
};
